import React, { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import {
    Paper,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Box,
    Pagination,
    SelectChangeEvent,
    Chip
} from '@mui/material';
import { StockService } from '../../services/stock.service';
import { ItemService } from '../../services/item.service';
import { getStoresByInstitutionId } from '../../services/store.service';
import { Item } from '../../interfaces/item.interface';
import { Store } from '../../interfaces/store.interface';
import { StockHistory as StockHistoryModel } from '../model/stock-history';
import { useToast } from '../../context/ToastContext';

const StockHistory: React.FC = () => {
    const toast = useToast();
    const [items, setItems] = useState<Item[]>([]);
    const [stores, setStores] = useState<Store[]>([]);
    const [history, setHistory] = useState<StockHistoryModel[]>([]);
    const [selectedItemId, setSelectedItemId] = useState<string>('');
    const [selectedStoreId, setSelectedStoreId] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [initialLoading, setInitialLoading] = useState<boolean>(true);
    const [pageInfo, setPageInfo] = useState({
        currentPage: 0,
        totalPages: 0,
        totalElements: 0
    });

    useEffect(() => {
        fetchStores();
        fetchItems();
    }, []);

    useEffect(() => {
        if (selectedStoreId && selectedItemId) {
            fetchHistory(selectedStoreId, selectedItemId, 0);
        } else {
            setHistory([]);
        }
    }, [selectedStoreId, selectedItemId]);

    const fetchStores = async () => {
        try {
            const response = await getStoresByInstitutionId(0, 100);
            setStores(response.content);
        } catch (err) {
            toast.showApiError(err, 'Failed to load stores');
        } finally {
            setInitialLoading(false);
        }
    };

    const fetchItems = async () => {
        try {
            const response = await ItemService.getItemsByInstitution(0, 100);
            setItems(response.content);
        } catch (err) {
            toast.showApiError(err, 'Failed to load items');
        }
    };

    const fetchHistory = async (storeId: string, itemId: string, page: number) => {
        setLoading(true);
        try {
            const response = await StockService.getStockHistory(storeId, itemId, page);
            setHistory(response.content);
            setPageInfo({
                currentPage: response.number,
                totalPages: response.totalPages,
                totalElements: response.totalElements
            });
        } catch (error) {
            console.error('Error fetching stock history:', error);
            setHistory([]);
        } finally {
            setLoading(false);
        }
    };

    const handleStoreChange = (event: SelectChangeEvent) => {
        setSelectedStoreId(event.target.value);
        setPageInfo(prev => ({ ...prev, currentPage: 0 }));
    };

    const handleItemChange = (event: SelectChangeEvent) => {
        setSelectedItemId(event.target.value);
        setPageInfo(prev => ({ ...prev, currentPage: 0 }));
    };

    const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
        setPageInfo(prev => ({ ...prev, currentPage: page - 1 }));
        if (selectedStoreId && selectedItemId) {
            fetchHistory(selectedStoreId, selectedItemId, page - 1);
        }
    };

    const formatDate = (dateString: string) => {
        return format(parseISO(dateString), 'dd-MM-yyyy HH:mm');
    };

    if (initialLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                Stock History
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <FormControl sx={{ width: '300px' }}>
                    <InputLabel>Select Store</InputLabel>
                    <Select
                        value={selectedStoreId}
                        label="Select Store"
                        onChange={handleStoreChange}
                        disabled={stores.length === 0}
                    >
                        {stores.map(store => (
                            <MenuItem key={store.id} value={store.id || ''}>
                                {store.storeName}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl sx={{ width: '300px' }}>
                    <InputLabel>Select Item</InputLabel>
                    <Select
                        value={selectedItemId}
                        label="Select Item"
                        onChange={handleItemChange}
                        disabled={items.length === 0}
                    >
                        {items.map(item => (
                            <MenuItem key={item.id} value={item.id}>
                                {item.itemName}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {!selectedStoreId || !selectedItemId ? (
                <Typography color="text.secondary" sx={{ textAlign: 'center', my: 4 }}>
                    Please select both a store and an item to view stock history.
                </Typography>
            ) : loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <TableContainer component={Paper} sx={{ mb: 3 }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                    <TableCell>#</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Change Type</TableCell>
                                    <TableCell>Amount</TableCell>
                                    <TableCell>Previous Qty</TableCell>
                                    <TableCell>New Qty</TableCell>
                                    <TableCell>Source</TableCell>
                                    <TableCell>Remarks</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {history.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">
                                            No stock history found
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    history.map((entry, index) => (
                                        <TableRow key={entry.id}>
                                            <TableCell>{index + 1 + pageInfo.currentPage * 10}</TableCell>
                                            <TableCell>{formatDate(entry.createdAt)}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={entry.changeType === 'STOCK_IN' ? 'Stock In' : 'Stock Out'}
                                                    color={entry.changeType === 'STOCK_IN' ? 'success' : 'error'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>{entry.amount}{entry.unitSymbol ? ` ${entry.unitSymbol}` : ''}</TableCell>
                                            <TableCell>{entry.previousQty}{entry.unitSymbol ? ` ${entry.unitSymbol}` : ''}</TableCell>
                                            <TableCell>{entry.newQty}{entry.unitSymbol ? ` ${entry.unitSymbol}` : ''}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={entry.source}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>{entry.remarks || '-'}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {pageInfo.totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <Pagination
                                count={pageInfo.totalPages}
                                page={pageInfo.currentPage + 1}
                                onChange={handlePageChange}
                                color="primary"
                            />
                        </Box>
                    )}
                </>
            )}
        </Paper>
    );
};

export default StockHistory;
